import {Label, RelativeTime, VisuallyHidden} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {CodeIcon} from '@primer/octicons-react'
import {MessageCircleIcon} from '@github-ui/custom-icons/MessageCircleIcon'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {clsx} from 'clsx'
import type {RepoInfo} from '../../../hooks/use-repo-info'
import styles from './SessionsNavGroupHovercard.module.css'

export interface SessionsNavGroupHovercardProps {
  repoNwo: string
  sessionCount: number
  latestSessionUpdatedAt?: string
  repoInfo?: RepoInfo
  isRepoInfoFetching: boolean
}

export const SessionsNavGroupHovercard = ({
  repoNwo,
  sessionCount,
  latestSessionUpdatedAt,
  repoInfo,
  isRepoInfoFetching,
}: SessionsNavGroupHovercardProps) => {
  const sessionCountLabel = `${sessionCount} recent ${sessionCount === 1 ? 'session' : 'sessions'}`
  const visibility = repoInfo?.visibility
    ? `${repoInfo.visibility.charAt(0).toUpperCase()}${repoInfo.visibility.slice(1)}`
    : undefined
  return (
    <>
      <div className={styles.header} data-testid="repo-hovercard-header">
        {isRepoInfoFetching ? (
          // Sized inline rather than by class: `.skeleton` in @github-ui/skeleton sets
          // `height: 1.2em` at the same specificity, so a class here would win or lose
          // on emitted source order and could silently shrink the row below the Label.
          <LoadingSkeleton
            variant="pill"
            height="var(--base-size-20)"
            width="var(--base-size-48)"
            className={clsx(styles.status, styles.visibilitySkeleton)}
            data-testid="repo-hovercard-visibility-skeleton"
            aria-hidden="true"
          />
        ) : visibility ? (
          <Label variant="secondary" className={styles.status}>
            {visibility}
          </Label>
        ) : (
          // Hold the header height when the API returns no visibility value.
          <Label
            variant="secondary"
            className={clsx(styles.status, styles.visibilityPlaceholder)}
            aria-hidden="true"
            data-testid="repo-hovercard-visibility-placeholder"
          >
            {'\u00a0'}
          </Label>
        )}
        {latestSessionUpdatedAt && (
          <>
            {/* The header puts the timestamp ahead of the repository name, so name what it
                measures rather than leaving the description to open with a bare "2h". */}
            <VisuallyHidden>Last session </VisuallyHidden>
            <RelativeTime
              noTitle
              format="micro"
              datetime={latestSessionUpdatedAt}
              className={styles.timestamp}
              data-testid="repo-hovercard-timestamp"
            />
          </>
        )}
      </div>
      <div className={styles.title} data-testid="repo-hovercard-title">
        {repoNwo}
      </div>
      {/* The session count is the only row below the title that always has a value, so it leads
          every optional field. A missing field then moves only the bottom edge, not visible rows. */}
      <ul className={styles.metaList}>
        <li className={styles.metaRow} data-testid="repo-hovercard-session-count">
          <MessageCircleIcon size={16} className={styles.metaIcon} />
          <span className={styles.metaValue}>{sessionCountLabel}</span>
        </li>
        {isRepoInfoFetching ? (
          <li className={styles.metaRow} aria-hidden="true">
            <CodeIcon size={16} className={clsx(styles.metaIcon, styles.loadingMetaIcon)} />
            <SkeletonText
              size="bodySmall"
              className={styles.languageSkeleton}
              data-testid="repo-hovercard-language-skeleton"
            />
          </li>
        ) : (
          repoInfo?.primaryLanguage && (
            <li className={styles.metaRow} data-testid="repo-hovercard-language">
              <CodeIcon size={16} className={styles.metaIcon} />
              <span className={styles.metaValue}>{repoInfo.primaryLanguage}</span>
            </li>
          )
        )}
      </ul>
      {isRepoInfoFetching ? (
        <SkeletonText
          size="bodySmall"
          className={styles.descriptionSkeleton}
          data-testid="repo-hovercard-description-skeleton"
          aria-hidden="true"
        />
      ) : (
        repoInfo?.description && <div className={styles.description}>{repoInfo.description}</div>
      )}
    </>
  )
}
